import { computed } from '@ember/object';
import { isPresent } from '@ember/utils';
import Model, { attr, belongsTo } from '@ember-data/model';
import { buildValidations, validator } from 'ember-cp-validations';
import moment from 'moment';

import { BRAND_DNA_MAX_PROFILES } from 'social-listening/utils/constants';

import type GroupModel from 'later/models/group';
import type SocialProfileModel from 'later/models/social-profile';
import type { Moment } from 'moment';

export type BrandDnaReportRaw = {
  title: string | null;
  primary_profiles: string[] | null;
  secondary_profiles: string[] | null;
  start_date: string | null;
  end_date: string | null;
};

export type ProfileTag = {
  listener_id: string;
  listener_identifier_id: string;
};

export enum BrandDnaReportStatus {
  BrandDnaModelFailed = 'brand_dna_model_failed',
  BrandDnaModelProcessing = 'brand_dna_model_processing',
  Completed = 'completed',
  CvModelFailed = 'cv_model_failed',
  CvModelProcessing = 'cv_model_processing',
  Failed = 'failed',
  Received = 'received',
  NotEnoughMedias = 'not_enough_medias'
}

export type BrandDnaReportStatusType = `${BrandDnaReportStatus}`;

const Validations = buildValidations({
  title: [
    validator('presence', {
      presence: true,
      message: 'social-listening.brand_dna.create.form.errors.name.empty'
    }),
    validator('length', {
      max: 30,
      message: 'social-listening.brand_dna.create.form.errors.name.length'
    })
  ],
  startDate: [
    validator('presence', {
      presence: true,
      message: 'social-listening.brand_dna.create.form.errors.date.empty'
    })
  ],
  endDate: [
    validator('presence', {
      presence: true,
      message: 'social-listening.brand_dna.create.form.errors.date.empty'
    })
  ],
  primaryProfiles: [
    validator('length', {
      min: 1,
      message: 'social-listening.brand_dna.create.form.errors.profiles.empty'
    }),
    validator('length', {
      max: BRAND_DNA_MAX_PROFILES,
      message: 'social-listening.brand_dna.create.form.errors.profiles.max_count'
    })
  ],
  secondaryProfiles: [
    validator('length', {
      max: BRAND_DNA_MAX_PROFILES,
      message: 'social-listening.brand_dna.create.form.errors.profiles.max_count'
    })
  ]
});

export default class BrandDnaReportModel extends Model.extend(Validations) {
  @belongsTo('group') declare group: GroupModel;
  @belongsTo('socialProfile') declare socialProfile: SocialProfileModel;

  @attr('string', { readOnly: true }) declare createdAt: string;
  @attr('string', { readOnly: true }) declare updatedAt: string;
  @attr('string') declare startDate: string;
  @attr('string') declare endDate: string;
  @attr('string') declare title: string;
  @attr('array') declare primaryProfiles: ProfileTag[];
  @attr('array') declare secondaryProfiles: ProfileTag[];
  @attr('string', { readOnly: true }) declare latestVersionId: string;
  @attr('string', { readOnly: true }) declare status: BrandDnaReportStatus;
  @attr({ readOnly: true }) declare data: Record<string, { total_medias: number; tagged_medias: number }>;

  get createdAtMoment(): Moment {
    return moment.unix(Number(this.createdAt));
  }

  get updatedAtMoment(): Moment {
    return moment.unix(Number(this.updatedAt));
  }

  get startDateMoment(): Moment {
    return moment.unix(moment(this.startDate).unix());
  }

  get endDateMoment(): Moment {
    return moment.unix(moment(this.endDate).unix());
  }

  get hasContent(): boolean {
    return Boolean(isPresent(this.title) && this.primaryProfiles.length > 0 && this.startDate && this.endDate);
  }

  @computed('primaryProfiles')
  get primaryProfilesTags(): Record<string, ProfileTag> {
    return this.primaryProfiles.reduce(
      (acc, profile) => {
        acc[profile.listener_identifier_id] = profile;
        return acc;
      },
      {} as Record<string, ProfileTag>
    );
  }

  @computed('secondaryProfiles')
  get secondaryProfilesTags(): Record<string, ProfileTag> {
    return this.secondaryProfiles.reduce(
      (acc, profile) => {
        acc[profile.listener_identifier_id] = profile;
        return acc;
      },
      {} as Record<string, ProfileTag>
    );
  }

  @computed('primaryProfiles')
  get primaryProfilesIdentifiers(): string[] {
    return this.primaryProfiles?.map((profile) => profile.listener_identifier_id);
  }

  @computed('secondaryProfiles')
  get secondaryProfilesIdentifiers(): string[] {
    return this.secondaryProfiles?.map((profile) => profile.listener_identifier_id);
  }

  @computed('status')
  get isBackfilling(): boolean {
    return this.status !== BrandDnaReportStatus.Completed && this.status !== BrandDnaReportStatus.Failed;
  }

  @computed('status')
  get isGeneratingReport(): boolean {
    const gatheringReportStatuses = [
      BrandDnaReportStatus.Received,
      BrandDnaReportStatus.CvModelProcessing,
      BrandDnaReportStatus.BrandDnaModelProcessing
    ];
    return gatheringReportStatuses.includes(this.status);
  }

  @computed('status')
  get hasError(): boolean {
    const errorStatuses = [
      BrandDnaReportStatus.CvModelFailed,
      BrandDnaReportStatus.BrandDnaModelFailed,
      BrandDnaReportStatus.Failed,
      BrandDnaReportStatus.NotEnoughMedias
    ];
    return errorStatuses.includes(this.status);
  }

  get profileNames(): string[] {
    return [...this.primaryProfilesIdentifiers, ...this.secondaryProfilesIdentifiers];
  }
}
